/**
 * ActivityVerdictTab — TICKET 2
 *
 * Renders the Proof-of-Activity verdict table for the manager's tenant.
 * Wired to GET /api/manager/activity-verdict. Pins INSUFFICIENT rows to
 * the top (server already sorts this way) and surfaces an "Escalate"
 * affordance per row.
 *
 * Bryan's locked doctrine (work/team_principles/revenue_activity.md):
 *   "Team members must generate revenue on plan or show proof-of-activity
 *    efforts, with no hiding allowed; otherwise, they retool, regroup, or
 *    modify approach."
 *
 * The component cannot be hidden by manager preference. The parent (the
 * ManagerHub) always renders this tab and includes the INSUFFICIENT count
 * in the tab label so the manager sees the named reps with no opt-out.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/session";

export type VerdictPeriod = "day" | "week" | "month" | "quarter";

interface RepVerdict {
  user_id: string;
  name: string;
  role: "bdr" | "ae";
  period: VerdictPeriod;
  period_start: string;
  period_end: string;
  revenue_target: number;
  revenue_to_date: number;
  revenue_target_prorated: number;
  on_plan: boolean;
  dials: number;
  conversations: number;
  meetings: number;
  meetings_held: number;
  emails: number;
  linkedin_dms: number;
  dials_floor: number;
  conversations_floor: number;
  meetings_floor: number;
  proof_of_activity_floor_met: boolean;
  verdict: "ON_PLAN" | "PROOF_OK" | "INSUFFICIENT";
  escalate: boolean;
  reasons: string[];
}

export interface VerdictResponse {
  period: VerdictPeriod;
  period_start: string;
  period_end: string;
  today: string;
  floors: { bdr: any; ae: any };
  reps: RepVerdict[];
  summary: { onPlan: number; proofOk: number; insufficient: number };
}

function fmtMoney(n: number): string {
  if (!isFinite(n) || n === 0) return "$0";
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${Math.round(n)}`;
}

const VERDICT_LABEL: Record<RepVerdict["verdict"], string> = {
  ON_PLAN: "ON PLAN",
  PROOF_OK: "PROOF OK",
  INSUFFICIENT: "INSUFFICIENT",
};

const VERDICT_BG: Record<RepVerdict["verdict"], string> = {
  ON_PLAN: "#dcfce7",
  PROOF_OK: "#fef9c3",
  INSUFFICIENT: "#fee2e2",
};
const VERDICT_FG: Record<RepVerdict["verdict"], string> = {
  ON_PLAN: "#166534",
  PROOF_OK: "#854d0e",
  INSUFFICIENT: "#991b1b",
};

/**
 * Pull the verdict from the server. Exported so the parent tab strip can
 * pre-fetch and use the INSUFFICIENT count to badge the tab label without
 * mounting this component.
 */
export async function loadVerdict(period: VerdictPeriod): Promise<VerdictResponse | null> {
  try {
    const r = await api<VerdictResponse>(`/api/manager/activity-verdict?period=${period}`);
    return r ?? null;
  } catch (e) {
    console.error("[ActivityVerdictTab] load failed:", e);
    return null;
  }
}

interface Props {
  initialPeriod?: VerdictPeriod;
  /**
   * Called once the server returns so the parent can update the tab badge
   * count without a second request.
   */
  onLoad?: (r: VerdictResponse) => void;
}

export default function ActivityVerdictTab({ initialPeriod = "day", onLoad }: Props) {
  const [period, setPeriod] = useState<VerdictPeriod>(initialPeriod);
  const [data, setData] = useState<VerdictResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    loadVerdict(period).then((r) => {
      if (cancelled) return;
      setLoading(false);
      if (!r) {
        setError("Could not load verdict. Try again.");
        return;
      }
      setData(r);
      onLoad?.(r);
    });
    return () => { cancelled = true; };
  }, [period]);

  const reps = useMemo(() => data?.reps ?? [], [data]);
  const summary = data?.summary ?? { onPlan: 0, proofOk: 0, insufficient: 0 };

  return (
    <div className="mx-auto max-w-6xl px-4 py-6" data-testid="activity-verdict-tab">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold" style={{ color: "#0B0B0F", fontFamily: "DM Sans, sans-serif" }}>
            Daily proof-of-activity verdict
          </h2>
          <p className="mt-1 text-xs text-neutral-500 max-w-2xl">
            Bryan's rule: revenue on plan OR proof-of-activity levels met. No hiding allowed —
            INSUFFICIENT rows are escalated and the manager cannot dismiss this view.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-neutral-500">Period</label>
          <select
            data-testid="verdict-period-select"
            className="rounded border border-neutral-300 bg-white px-2 py-1 text-sm"
            value={period}
            onChange={(e) => setPeriod(e.target.value as VerdictPeriod)}
          >
            <option value="day">Today (DTD)</option>
            <option value="week">Week to date</option>
            <option value="month">Month to date</option>
            <option value="quarter">Quarter to date</option>
          </select>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-2 text-xs">
        <SummaryPill label="On plan"      count={summary.onPlan}       bg="#dcfce7" fg="#166534" />
        <SummaryPill label="Proof OK"     count={summary.proofOk}      bg="#fef9c3" fg="#854d0e" />
        <SummaryPill label="Insufficient" count={summary.insufficient} bg="#fee2e2" fg="#991b1b" />
      </div>

      {loading && (
        <div className="rounded-lg border border-neutral-200 bg-white p-6 text-sm text-neutral-500">
          Loading verdict...
        </div>
      )}
      {error && !loading && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800" data-testid="verdict-error">
          {error}
        </div>
      )}

      {!loading && !error && reps.length === 0 && (
        <div className="rounded-lg border border-neutral-200 bg-white p-6 text-sm text-neutral-500">
          No reps in this tenant yet.
        </div>
      )}

      {!loading && !error && reps.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-neutral-200 bg-white shadow-sm">
          <table className="min-w-full text-sm">
            <thead className="bg-neutral-50 text-left text-xs uppercase text-neutral-500">
              <tr>
                <th className="px-3 py-2">Rep</th>
                <th className="px-3 py-2">Verdict</th>
                <th className="px-3 py-2">Revenue</th>
                <th className="px-3 py-2">Dials</th>
                <th className="px-3 py-2">Conv.</th>
                <th className="px-3 py-2">Meetings</th>
                <th className="px-3 py-2">Reasons</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {reps.map((r) => (
                <tr
                  key={r.user_id}
                  data-testid={`verdict-row-${r.user_id}`}
                  data-verdict={r.verdict}
                  className="border-t border-neutral-100 align-top"
                >
                  <td className="px-3 py-2">
                    <div className="font-medium text-neutral-900">{r.name}</div>
                    <div className="text-[11px] uppercase text-neutral-500">{r.role}</div>
                  </td>
                  <td className="px-3 py-2">
                    <VerdictPill verdict={r.verdict} />
                  </td>
                  <td className="px-3 py-2 text-neutral-800">
                    <div>{fmtMoney(r.revenue_to_date)}</div>
                    <div className="text-[11px] text-neutral-500">
                      target {fmtMoney(r.revenue_target_prorated)} of {fmtMoney(r.revenue_target)}
                    </div>
                  </td>
                  <NumberCell value={r.dials} floor={r.dials_floor} />
                  <NumberCell value={r.conversations} floor={r.conversations_floor} />
                  <NumberCell value={r.meetings} floor={r.meetings_floor} />
                  <td className="px-3 py-2 text-xs text-neutral-700">
                    {r.reasons.length === 0 ? <span className="text-neutral-400">—</span> :
                      <ul className="list-disc pl-4">{r.reasons.map((x, i) => <li key={i}>{x}</li>)}</ul>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {r.escalate && (
                      <a
                        href={`#/dashboards/manager?as=manager&rep=${r.user_id}`}
                        className="inline-flex items-center rounded bg-[#0B0B0F] px-2.5 py-1 text-xs font-medium text-white hover:bg-neutral-800"
                        data-testid={`verdict-escalate-${r.user_id}`}
                      >
                        Escalate
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function VerdictPill({ verdict }: { verdict: RepVerdict["verdict"] }) {
  return (
    <span
      data-testid={`verdict-pill-${verdict}`}
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        background: VERDICT_BG[verdict],
        color: VERDICT_FG[verdict],
        letterSpacing: 0.2,
      }}
    >
      {VERDICT_LABEL[verdict]}
    </span>
  );
}

function SummaryPill({ label, count, bg, fg }: { label: string; count: number; bg: string; fg: string }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "4px 10px", borderRadius: 999, background: bg, color: fg, fontWeight: 600,
    }}>
      <span>{label}</span>
      <span style={{ fontVariantNumeric: "tabular-nums" }}>{count}</span>
    </span>
  );
}

function NumberCell({ value, floor }: { value: number; floor: number }) {
  const under = floor > 0 && value < floor;
  return (
    <td className="px-3 py-2 font-medium" style={{ color: under ? "#991b1b" : "#0B0B0F" }}>
      {value.toLocaleString()}
      <span className="ml-1 text-[11px] font-normal text-neutral-500">/ {floor.toLocaleString()}</span>
    </td>
  );
}
